import React, { useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Search } from 'lucide-react';
import { getPlaylistForTeam } from '../config/playlist-links';

const defaultPlaylist = 'https://open.spotify.com/embed/playlist/37i9dQZF1DX4eRPd9frC1m';

const nflTeams = [
  'Arizona Cardinals','Atlanta Falcons','Baltimore Ravens','Buffalo Bills','Carolina Panthers',
  'Chicago Bears','Cincinnati Bengals','Cleveland Browns','Dallas Cowboys','Denver Broncos',
  'Detroit Lions','Green Bay Packers','Houston Texans','Indianapolis Colts','Jacksonville Jaguars',
  'Kansas City Chiefs','Las Vegas Raiders','Los Angeles Chargers','Los Angeles Rams','Miami Dolphins',
  'Minnesota Vikings','New England Patriots','New Orleans Saints','New York Giants','New York Jets',
  'Philadelphia Eagles','Pittsburgh Steelers','San Francisco 49ers','Seattle Seahawks','Tampa Bay Buccaneers',
  'Tennessee Titans','Washington Commanders'
];

const mlbTeams = [
  'San Francisco Giants','LA Dodgers','Los Angeles Dodgers','San Diego Padres','NY Mets','New York Mets',
  'Colorado Rockies','Arizona Diamondbacks','Washington Nationals','Philadelphia Phillies','Atlanta Braves',
  'Miami Marlins','Pittsburgh Pirates','St Louis Cardinals','St. Louis Cardinals','Milwaukee Brewers',
  'Cincinnati Reds','NY Yankees','New York Yankees','Chicago Cubs','Texas Rangers','Seattle Mariners',
  'Boston Red Sox','Los Angeles Angels','Houston Astros','Oakland Athletics','Toronto Blue Jays',
  'Tampa Bay Rays','Cleveland Guardians','Baltimore Orioles','Minnesota Twins','KC Royals',
  'Kansas City Royals','Detroit Tigers','Chicago White Sox'
];

const nhlTeams = [
  'Philadelphia Flyers','Winnipeg Jets','Washington Capitals','Washington Capitols','Vegas Golden Knights',
  'Vancouver Canucks','Toronto Maple Leafs','Tampa Bay Lightning','St Louis Blues','St. Louis Blues',
  'Seattle Kraken','New York Rangers','San Jose Sharks','Pittsburgh Penguins','Ottawa Senators',
  'Chicago Blackhawks','New York Islanders','New Jersey Devils','Nashville Predators','Minnesota Wild',
  'Montreal Canadiens','Colorado Avalanche','Dallas Stars','Detroit Red Wings','Detroit RedWings',
  'Los Angeles Kings','Columbus Blue Jackets','Edmonton Oilers','Boston Bruins','Florida Panthers',
  'Carolina Hurricanes','Calgary Flames','Buffalo Sabres','Anaheim Ducks','Utah Mammoth'
];

const nbaTeams = [
  'Cleveland Cavaliers','Cleveland Cavs','Washington Wizards','Charlotte Hornets','Charolette Hornets',
  'Milwaukee Bucks','Indiana Pacers','Detroit Pistons','Philadelphia 76ers','Miami Heat',
  'Minnesota Timberwolves','New Orleans Pelicans','Sacramento Kings','Boston Celtics','Toronto Raptors',
  'Portland Trail Blazers','Dallas Mavericks','Houston Rockets','San Antonio Spurs','Phoenix Suns',
  'Memphis Grizzlies','Utah Jazz','Denver Nuggets','Golden State Warriors','LA Clippers',
  'Los Angeles Clippers','LA Lakers','Los Angeles Lakers','Brooklyn Nets','New York Knicks',
  'Chicago Bulls','Orlando Magic','Atlanta Hawks','OKC Thunder','Oklahoma City Thunder'
];

const collegeTeams = [
  'Oklahoma','Oklahoma Sooners','Oklahoma State','Oklahoma State Cowboys'
];

export default function Playlists() {
  const { selectedTeam, teamColors } = useTheme();
  const [query, setQuery] = useState('');
  const primary = teamColors?.primary || '#0ea5e9';
  const secondary = teamColors?.secondary || '#f97316';

  const highlightSrc = useMemo(() => {
    if (!selectedTeam?.name) return defaultPlaylist;
    return getPlaylistForTeam(selectedTeam.name) || defaultPlaylist;
  }, [selectedTeam?.name]);

  const teams = useMemo(() => {
    const allTeams = [...nflTeams, ...mlbTeams, ...nhlTeams, ...nbaTeams, ...collegeTeams];
    const list = allTeams.filter(t => t !== selectedTeam?.name);
    if (!query.trim()) return list;
    return list.filter(t => t.toLowerCase().includes(query.toLowerCase()));
  }, [query, selectedTeam?.name]);

  return (
    <div className="min-h-screen pt-20 bg-white"
      style={{
        background: selectedTeam
          ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 50%, ${primary} 100%)`
          : undefined
      }}
    >
      {/* Hero */}
      <section className="py-10 text-white" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-extrabold">Team Playlists</h1>
          <p className="mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>Vibe for game day. Your team up top; search the rest below.</p>
        </div>
      </section>

      <section className="py-8" style={{ background: 'rgba(255,255,255,0.85)' }}>
        <div className="max-w-6xl mx-auto px-4 space-y-8">
          {/* Highlight */}
          <div className="card p-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">{selectedTeam?.name || 'Featured'} Playlist</h2>
            <div className="aspect-video w-full rounded-xl overflow-hidden border">
              <iframe
                title="Highlighted Playlist"
                src={highlightSrc}
                width="100%"
                height="100%"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search teams..."
              className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => {
              const src = getPlaylistForTeam(team) || defaultPlaylist;
              return (
                <div key={team} className="card p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{team}</h3>
                  <div className="aspect-video w-full rounded-xl overflow-hidden border">
                    <iframe
                      title={`${team} Playlist`}
                      src={src}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}


